import React, {useContext, useEffect, useState} from "react";
import styled from "styled-components";
import {apiRequest, formatDate, formatFileSize, snakeToNormalCase, toast, toTitle} from "@/Helpers/general";
import {getUserAvatar} from "@/Helpers/user";
import ContentLoadingTemplate from "@/Components/Templates/ContentLoadingTemplate";
import {IL_ROWS_NO} from "@/Config/constants";
import InfiniteScroll from "react-infinite-scroll-component";
import {AppContext} from "@/Components/Context/AppContext";
import {mapFieldsData, mapFieldsTable} from "@/Helpers/forms";

const AuditWrap = styled.div`
    .audits-list-wrap {
        border-right: 1px solid var(--gray-200);
        padding-right: 10px;

        .audit-item-wrap {
            padding: 5px 10px;
            opacity: 0.7;
            cursor: pointer;
            border-radius: 4px;

            &:hover, &.active {
                opacity: 1;
                background-color: var(--gray-200);
            }

            .audit-user, .audit-date, .audit-desc {
                font-size: 13px;
            }

            .audit-user {
                font-weight: bold;
            }

            .audit-desc {
                height: 1.5rem;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }
    }

    .audit-preview {
        .user-info {
            .user-name {
                font-weight: bold;
            }

            .audit-date {
                font-size: 13px;
                color: var(--gray-700);
            }
        }

        .audit-description {
            background: var(--gray-100);
            padding: 0.5rem 1rem 1rem 1rem;
            border-radius: 4px;
        }
    }

    .loader-wrap {
        position: relative;
        height: 60px;
    }

    .no-audits {
        padding: 1.5rem;
    }
`;

/**
 * Component which renders a case audit (logs list)
 */
const Audit = ({caseId}) => {
    const {toastRef} = useContext(AppContext);

    const [audits, setAudits] = useState([]);
    const [selectedAuditId, setSelectedAuditId] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMoreAudits, setHasMoreAudits] = useState(true);

    /**
     * Generate a preview of an audit log
     */
    const generateAuditPreview = () => {
        let getCurrentAudit = audits?.find((audit) => audit.id === selectedAuditId);

        if (getCurrentAudit) {
            return (
                <>
                    <div className="user-info mb-3 flex">
                        {getUserAvatar(getCurrentAudit.user || {})}

                        <div>
                            <div className="user-name">{getCurrentAudit.user ? getCurrentAudit.user?.name : 'System'}</div>
                            <div className="audit-date">{formatDate(getCurrentAudit.created_at)}</div>
                        </div>
                    </div>

                    <div className="audit-description">{getCurrentAudit.detail}</div>

                    {getCurrentAudit.type === 'create_file' && (
                        <table className="simple-table">
                            <tbody>
                            {getCurrentAudit?.more_detail?.file_info.map((file) => {
                                return (
                                    <tr key={file.id}>
                                        <td className="file-name">{file.name}</td>
                                        <td>{formatFileSize(file.size)}</td>
                                        <td>{file.mime}</td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    )}

                    {getCurrentAudit.type === 'update_case' && (
                        <table className="simple-table">
                            <tbody>
                            {mapFieldsData(getCurrentAudit).map((field) => {
                                return (
                                    <tr key={field.name}>
                                        <td className="file-name">{field.name}</td>
                                        <td>{field.value}</td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    )}

                    {getCurrentAudit.type === 'rule_applied' && (
                        <table className="simple-table">
                            <tbody>
                            {Object.keys(getCurrentAudit.more_detail.data).map((relation) => (
                                <>
                                    <tr>
                                        <th align="left" colSpan={3}>
                                            {toTitle(relation)}
                                        </th>
                                    </tr>
                                    {mapFieldsTable(getCurrentAudit.more_detail.data[relation]).map((field) => (
                                        <tr key={relation + '-' + field.name}>
                                            <td>{field.name}</td>
                                            <td>{field.type}</td>
                                            <td>{field.value}</td>
                                        </tr>
                                    ))}
                                    <br/>
                                </>
                            ))}
                            </tbody>
                        </table>
                    )}

                    {getCurrentAudit.type === 'offer_amended' && (
                        <table className="simple-table">
                            <tbody>
                            <>
                                <tr>
                                    <th align="left" colSpan={2}>
                                        Rationale
                                    </th>
                                </tr>
                                <tr>
                                    <td align="left">
                                        {getCurrentAudit.more_detail.data.rationale}
                                    </td>
                                </tr>
                                <br/>
                                <tr>
                                    <th align="left" colSpan={2}>
                                        Before
                                    </th>
                                </tr>
                                {Object.keys(getCurrentAudit.more_detail.data.before).sort().map((item) =>
                                    <tr key={`before-${item}`}>
                                        <td>
                                            {snakeToNormalCase(item)}
                                        </td>
                                        <td>
                                            {getCurrentAudit.more_detail.data.before[item]}
                                        </td>
                                    </tr>)
                                }
                                <br/>
                                <tr>
                                    <th align="left" colSpan={2}>
                                        After
                                    </th>
                                </tr>
                                {Object.keys(getCurrentAudit.more_detail.data.after).sort().map((item) =>
                                    <tr key={`after-${item}`}>
                                        <td>
                                            {snakeToNormalCase(item)}
                                        </td>
                                        <td>
                                            {getCurrentAudit.more_detail.data.after[item]}
                                        </td>
                                    </tr>)
                                }
                            </>
                            </tbody>
                        </table>
                    )}

                    {getCurrentAudit.type === 'create_note' && (
                        <div className="p-3">{getCurrentAudit.more_detail?.description}</div>
                    )}
                </>
            );
        }
    };

    /**
     * Get audits from the API
     */
    const loadAudits = async () => {
        let requestParams = {
            page: currentPage,
            limit: IL_ROWS_NO
        };

        apiRequest('get', 'case/' + caseId + '/audits', {
            data: requestParams,
            onSuccess: (response) => {
                if (response.result && response.result.length > 0) {
                    setAudits([...audits, ...response.result]);

                    if (currentPage == 1) {
                        setSelectedAuditId(response.result[0].id);
                    }

                    if (currentPage == response.pagination.totalPages) {
                        setHasMoreAudits(false);
                    }

                    setCurrentPage(currentPage + 1);

                } else {
                    setHasMoreAudits(false);
                }
            },
            onFailure: () => {
                setHasMoreAudits(false);
            }
        });
    };

    /**
     * Get the next page
     */
    const loadNextPage = () => {
        if (hasMoreAudits) {
            loadAudits();
        }
    };

    // init
    useEffect(() => {
        loadAudits();
    }, []);

    return (
        <AuditWrap>
            <div className="grid">
                <div className="col-6">
                    <div className="audits-list-wrap">
                        <InfiniteScroll
                            height="60vh"
                            dataLength={audits.length}
                            next={loadNextPage}
                            hasMore={hasMoreAudits}
                            loader={<div className="loader-wrap"><ContentLoadingTemplate/></div>}
                            endMessage={audits.length == 0 && <div className="no-audits">No audits found.</div>}
                        >
                            {audits.map((audit) => {
                                return (
                                    <div key={audit.id} className={'audit-item-wrap' + (selectedAuditId == audit.id ? ' active' : '')} onClick={() => setSelectedAuditId(audit.id)}>
                                        <div className="flex justify-content-between">
                                            <span className="audit-user">{audit.title}</span>
                                            <span className="audit-date">{formatDate(audit.created_at)}</span>
                                        </div>

                                        <div className="audit-desc">{audit.description}</div>
                                    </div>
                                );
                            })}
                        </InfiniteScroll>
                    </div>
                </div>

                <div className="col-6">
                    <div className="audit-preview">
                        {generateAuditPreview()}
                    </div>
                </div>
            </div>
            <hr/>
        </AuditWrap>
    );
};

export default Audit;
